import React from "react";
import { ICON } from "../../../assets";
import { price, round2 } from "../../../utils";
import { useAuth } from "../../Auth/useAuth";
import useTranslations from "../../Translations/useTranslations";
import UiButton from "../../ui/UiButton";
import UiIcon from "../../ui/UiIcon";
import UiTag from "../../ui/UiTag";
import { UI_TAG_TYPE } from "../../ui/UiTag/index";
import { useCart } from "../useCart";
import "./PackItems.css";
import { getPricesSteps } from "../../../utils_price";

function PackItems({ isQuickDispatchItem = false, disableControls = false }) {
  const [{ user }] = useAuth();
  const [{ current_cart }, cartSDK] = useCart(user.client_profile?.id);

  const trl = useTranslations();
  if (!current_cart) return null;
  const packsNames =
    current_cart.packs
      .filter((el) => el.name && el.qte_commande > 0)
      .map((pk) => pk.name) || [];
  const filteredPackNames = Object.keys(current_cart.products)?.filter(
    (key) => packsNames.includes(key) && current_cart.products[key].length > 0,
    // Filter quick dispatch items todo
  );
  if (filteredPackNames.length === 0) return null;

  const currentCartProductItems =
    filteredPackNames
      .map((key) => {
        return { [key]: current_cart.products[key] };
      })
      .flat()
      .reduce((a, b) => ({ ...a, ...b }), {}) || {};

  const _addPack = (packArt, products, qte_commande) => {
    // Pack in cart
    const packIndex = current_cart.packs.findIndex(
      (_pack) => _pack.name === packArt.name,
    );
    const packs = current_cart.packs;

    if (qte_commande > 0) {
      // Update cart
      const currentPack = { ...packArt, qte_commande };

      if (packIndex >= 0) {
        packs[packIndex] = currentPack;
      } else {
        // The pack doesnt exist yet
        packs.push(currentPack);
      }
    } else {
      // Remove pack from cart
      packs.splice(packIndex, 1);
      cartSDK.addInCart({
        creator: user.client_profile?.id,
        product: {
          [packArt.name]: [],
        },
      });

      return;
    }

    const packCart = products.map((art) => {
      let article = art.article;
      let remise =
        article.REMISE_FIXE > 0
          ? article.REMISE_FIXE
          : article.groupe_tarifaire.REMISE_FIXE > 0
            ? article.groupe_tarifaire.REMISE_FIXE
            : null;

      if (!remise || remise <= 0) {
        if (
          article.groupe_tarifaire.TRANCHE5 > 0 &&
          article.QTE * qte_commande >= article.groupe_tarifaire.TRANCHE5
        ) {
          remise = article.groupe_tarifaire.REMISE5;
        } else if (
          article.groupe_tarifaire.TRANCHE4 > 0 &&
          article.QTE * qte_commande >= article.groupe_tarifaire.TRANCHE4
        ) {
          remise = article.groupe_tarifaire.REMISE4;
        } else if (
          article.groupe_tarifaire.TRANCHE3 > 0 &&
          article.QTE * qte_commande >= article.groupe_tarifaire.TRANCHE3
        ) {
          remise = article.groupe_tarifaire.REMISE3;
        } else if (
          article.groupe_tarifaire.TRANCHE2 > 0 &&
          article.QTE * qte_commande >= article.groupe_tarifaire.TRANCHE2
        ) {
          remise = article.groupe_tarifaire.REMISE2;
        } else if (
          article.groupe_tarifaire.TRANCHE1 > 0 &&
          article.QTE * qte_commande >= article.groupe_tarifaire.TRANCHE1
        ) {
          remise = article.groupe_tarifaire.REMISE1;
        } else {
          remise = 0;
        }
      }

      return {
        article,
        qte_commande: article.QTE * qte_commande,
        custom_remise: remise,
      };
    });
    cartSDK.addPackInCart({ creator: user.client_profile?.id, packs });
    cartSDK.addInCart({
      creator: user.client_profile?.id,
      product: {
        [packArt.name]: packCart,
      },
    });
  };

  function discardPackFromCart(packArt, products, qte_commande) {
    cartSDK.removePackFromCart({
      creator: user.client_profile?.id,
      packName: packArt.name,
    });
    _addPack(packArt, products, qte_commande);
  }

  function renderItem(codgen, products) {
    const packArt = current_cart.packs.find((pack) => pack.name === codgen);

    if (!packArt) return null;
    const count = packArt.qte_commande;
    const minOrderCount = 1;

    function calculatePackPrice() {
      if (!packArt) return 0;
      const totalPrice = products.reduce((acc, product) => {
        const remise = product.custom_remise || 0;
        const qte = product?.qte_commande || 0;
        const numPacks = packArt.qte_commande || 1;
        const { priceStep, nbUnit } = getPricesSteps(product.article, qte / numPacks);
        const pub = priceStep
          ? priceStep.price / nbUnit
          : product?.article?.PUB || 0;
        const pubAfterRemise = pub - (pub * remise) / 100;
        return acc + pubAfterRemise * qte;
      }, 0);
      const discount =
        packArt.mt_remise > 0 ? packArt.mt_remise * packArt.qte_commande : 0;
      return totalPrice - discount;
    }

    const discountLabel =
      packArt.mt_remise > 0 && packArt.qte_commande > 0
        ? round2(packArt.mt_remise * packArt.qte_commande)
        : null;

    const currentPrice = price(calculatePackPrice());

    function emptyAction() {
      if (!window.confirm(trl.message("Cart.removePackOnAction"))) return;
      discardPackFromCart(packArt, products, -1);
    }

    function addAction() {
      _addPack(packArt, products, packArt.qte_commande + 1);
    }
    function removeAction() {
      if (packArt.qte_commande - 1 < 0) return;
      _addPack(packArt, products, packArt.qte_commande - 1);
    }

    function renderAddRemoveButtons() {
      const buttonStyle = {
        padding: "0 4px",
      };
      return (
        <React.Fragment>
          <div className="pack-add-remove-buttons">
            {!disableControls && (
              <UiButton
                content={
                  <UiIcon icon={ICON.minusIcon} alt="minus-icon" size={10} />
                }
                style={buttonStyle}
                onClick={removeAction}
                disabled={count <= minOrderCount}
              />
            )}
            <div className="pack-quantity" style={{ fontSize: "15px" }}>
              {count}
            </div>
            {!disableControls && (
              <UiButton
                content={
                  <UiIcon icon={ICON.plusIcon} alt="plus-icon" size={10} />
                }
                style={buttonStyle}
                onClick={addAction}
              />
            )}
          </div>
        </React.Fragment>
      );
    }

    function renderDiscountTag() {
      if (discountLabel) {
        return (
          <UiTag
            content={
              <span className="pack-tag">
                {discountLabel ? "-" + discountLabel + " € " : "-0 €"}
              </span>
            }
            uiTagType={UI_TAG_TYPE.highlightColor}
            style={{ marginRight: "5px" }}
          />
        );
      }
      return null;
    }

    function renderTrashIcon() {
      if (!disableControls) {
        return (
          <UiIcon
            icon={ICON.trashIcon}
            onClick={emptyAction}
            alt="trashIcon"
            size={22}
            style={{ cursor: "pointer", marginLeft: "10px" }}
          />
        );
      }
      return null;
    }

    return (
      <React.Fragment>
        <div className="pack-container">
          <div className="pack-info">
            {renderDiscountTag(discountLabel)}
            <span className="pack-name">{codgen}</span>
          </div>
          <div className="pack-controls">
            <div className="pack-actions">
              {renderAddRemoveButtons()}
              {renderTrashIcon()}
            </div>
            <div>{currentPrice && <span>{currentPrice} €</span>}</div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  function renderProducts(productList) {
    function renderProduct(product) {
      const { custom_remise, article, qte_commande } = product;
      const { REMISE_FIXE, groupe_tarifaire, TESTEUR_ou_PLV, CODART } = article;
      const remise =
        custom_remise >= 0
          ? custom_remise
          : REMISE_FIXE > 0
            ? REMISE_FIXE
            : groupe_tarifaire.REMISE_FIXE > 0
              ? groupe_tarifaire.REMISE_FIXE
              : null;
      const productName = trl.data.message("ART", "CODART", CODART, "LIBART");

      function renderTesterTag() {
        if (TESTEUR_ou_PLV === "TES") {
          return (
            <UiTag
              content={<span>{trl.message("MainPage.testerLabel")}</span>}
              uiTagType={UI_TAG_TYPE.critical}
              fontSize={10}
              fontWeight={700}
              style={{ padding: "4px 6px", color: "#fff" }}
            />
          );
        }
        return null;
      }

      function renderRemiseTag() {
        if (remise > 0) {
          return (
            <UiTag
              content={<span>-{remise}%</span>}
              uiTagType={UI_TAG_TYPE.highlightColor}
              fontSize={10}
              fontWeight={700}
              style={{ padding: "4px 6px" }}
            />
          );
        }
        return null;
      }

      function renderProductsTags() {
        return (
          <div className="pack-product-tags">
            {renderTesterTag()}
            {renderRemiseTag()}
          </div>
        );
      }

      return (
        <React.Fragment>
          <div className="pack-product-item">
            <div className="pack-product-info">
              {renderProductsTags()}
              <div className="pack-product-name">{productName}</div>
            </div>
            <div className="pack-product-quantity">x {qte_commande}</div>
          </div>
        </React.Fragment>
      );
    }

    return productList.map((el, idx) => (
      <React.Fragment key={idx}>{renderProduct(el)}</React.Fragment>
    ));
  }

  return (
    <React.Fragment>
      {filteredPackNames.map((codgen, i) => {
        return (
          <React.Fragment key={i}>
            {renderItem(codgen, currentCartProductItems[codgen])}
            {renderProducts(currentCartProductItems[codgen])}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
}

export default PackItems;
